import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import toast from '../../components/ui/Toast';
import NavigationSidebar from '../../components/NavigationSidebar';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import ProgressIndicator from './components/ProgressIndicator';
import CompanyProfileSection from './components/CompanyProfileSection';
import IndustrySelectionSection from './components/IndustrySelectionSection';
import TargetAudienceSection from './components/TargetAudienceSection';
import MarketingGoalsSection from './components/MarketingGoalsSection';
import AgentPersonalizationSection from './components/AgentPersonalizationSection';
import CompletionSummary from './components/CompletionSummary';
import AgentChatInterface from '../../components/AgentChatInterface';
import ErrorBoundary from '../../components/ErrorBoundary';


const OnboardingAgentInterface = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, currentOrganizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showCompletion, setShowCompletion] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'form'

  const steps = [
    {
      id: 1,
      title: 'Company Profile',
      description: 'Basic business information',
      icon: 'Building2',
      component: CompanyProfileSection
    },
    {
      id: 2,
      title: 'Industry Selection',
      description: 'Your business category',
      icon: 'Briefcase',
      component: IndustrySelectionSection
    },
    {
      id: 3,
      title: 'Target Audience',
      description: 'Define your customers',
      icon: 'Users',
      component: TargetAudienceSection
    },
    {
      id: 4,
      title: 'Marketing Goals',
      description: 'Objectives and budget',
      icon: 'Target',
      component: MarketingGoalsSection
    },
    {
      id: 5,
      title: 'Agent Personalization',
      description: 'Customize agent behavior',
      icon: 'Settings',
      component: AgentPersonalizationSection
    }
  ];

  useEffect(() => {
    if (user?.id) {
      loadExistingProfile();
    }
  }, [user]);

  const loadExistingProfile = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      
      if (data) {
        // Load existing profile data into form
        const profileData = {
          companyName: data?.company_name || '',
          businessSize: data?.business_size || '',
          foundingDate: data?.founding_date || '',
          organizationalStructure: data?.organizational_structure || '',
          industry: data?.industry || '',
          subIndustry: data?.sub_industry || '',
          targetAudience: data?.target_audience || '',
          customerPersonas: data?.customer_personas || [],
          marketSegments: data?.market_segments || [],
          marketingObjectives: data?.marketing_objectives || [],
          budgetAllocation: data?.budget_allocation || '',
          successMetrics: data?.success_metrics || [],
          communicationPreferences: data?.communication_preferences || {},
          automationLevel: data?.automation_level || 'balanced',
          integrationRequirements: data?.integration_requirements || []
        };
        setFormData(profileData);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast?.error('Failed to load existing profile');
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';
        if (!formData?.businessSize) newErrors.businessSize = 'Business size is required';
        break;
      case 2:
        if (!formData?.industry) newErrors.industry = 'Industry selection is required';
        break;
      case 3:
        if (!formData?.targetAudience?.trim()) newErrors.targetAudience = 'Target audience is required';
        break;
      case 4:
        if (!formData?.marketingObjectives || formData?.marketingObjectives?.length === 0) {
          newErrors.marketingObjectives = 'At least one marketing objective is required';
        }
        break;
      case 5:
        if (!formData?.automationLevel) newErrors.automationLevel = 'Automation level is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps?.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        handleSubmit();
      }
    } else {
      toast?.error('Please complete all required fields');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      toast?.error('Please complete all required fields');
      return;
    }

    setSaving(true);
    try {
      const profileUpdate = {
        company_name: formData?.companyName,
        business_size: formData?.businessSize,
        founding_date: formData?.foundingDate,
        organizational_structure: formData?.organizationalStructure,
        industry: formData?.industry,
        sub_industry: formData?.subIndustry,
        target_audience: formData?.targetAudience,
        customer_personas: formData?.customerPersonas || [],
        market_segments: formData?.marketSegments || [],
        marketing_objectives: formData?.marketingObjectives || [],
        budget_allocation: formData?.budgetAllocation,
        success_metrics: formData?.successMetrics || [],
        communication_preferences: formData?.communicationPreferences || {},
        automation_level: formData?.automationLevel,
        integration_requirements: formData?.integrationRequirements || [],
        onboarding_completed: true,
        updated_at: new Date()?.toISOString()
      };

      const { error } = await supabase
        ?.from('user_profiles')
        ?.update(profileUpdate)
        ?.eq('id', user?.id);

      if (error) throw error;

      toast?.success('Profile saved successfully!');
      setShowCompletion(true);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast?.error(error?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCompletionClose = () => {
    setShowCompletion(false);
    navigate('/dashboard');
  };

  const handleTutorialStart = () => {
    // Tutorial start logic - can be customized as needed
    console.log('Tutorial started');
  };

  if (loading) {
    return (
      <ErrorBoundary>
        <div className="flex h-screen bg-neutral-50">
          <NavigationSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-neutral-50">
        <Toaster position="top-right" />
        <NavigationSidebar />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 p-1.5">
                  <img 
                    src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                    alt="DraftIQ Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground font-heading truncate">
                    Onboarding Agent Interface
                  </h1>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Get personalized setup assistance or complete the onboarding form
                  </p>
                </div>
              </div>
              <TutorialAccessButton onTutorialStart={handleTutorialStart} />
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Tab Switcher */}
              <div className="bg-card border border-border rounded-xl p-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTab('chat')}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-250
                      ${activeTab === 'chat' ?'bg-primary text-primary-foreground shadow-sm' :'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                  >
                    <Icon name="MessageSquare" size={18} />
                    <span className="font-medium">Chat with Agent</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('form')}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-250
                      ${activeTab === 'form'
                        ? 'bg-primary text-primary-foreground shadow-sm' :'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                  >
                    <Icon name="FileText" size={18} />
                    <span className="font-medium">Onboarding Form</span>
                  </button>
                </div>
              </div>

              {/* Chat Interface */}
              {activeTab === 'chat' && (
                <div className="h-[600px]">
                  <AgentChatInterface
                    userId={user?.id}
                    agentId="onboarding"
                    agentName="Onboarding Agent"
                    agentIcon="Rocket"
                    agentDescription="Setup and configuration specialist"
                  />
                </div>
              )}

              {/* Onboarding Form */}
              {activeTab === 'form' && (
                <>
                  {/* Progress Indicator */}
                  <ProgressIndicator 
                    steps={steps} 
                    currentStep={currentStep} 
                    onStepClick={(step) => {
                      if (step < currentStep) {
                        setCurrentStep(step);
                      }
                    }}
                  />

                  {/* Current Step Content */}
                  <div className="bg-card rounded-xl border border-border shadow-warm p-4 sm:p-6 md:p-8">
                    {CurrentStepComponent && (
                      <CurrentStepComponent
                        formData={formData}
                        errors={errors}
                        onChange={handleFieldChange}
                      />
                    )}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between gap-4">
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      disabled={currentStep === 1 || saving}
                      className="flex items-center gap-2"
                    >
                      <Icon name="ChevronLeft" size={18} />
                      Previous
                    </Button>

                    <div className="text-sm text-muted-foreground">
                      Step {currentStep} of {steps?.length}
                    </div>

                    <Button
                      onClick={handleNext}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Icon name="Loader2" size={18} className="animate-spin" />
                          Saving...
                        </>
                      ) : currentStep === steps?.length ? (
                        <>
                          Complete Setup
                          <Icon name="Check" size={18} />
                        </>
                      ) : (
                        <>
                          Next
                          <Icon name="ChevronRight" size={18} />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </main>
        </div>

        {/* Completion Modal */}
        {showCompletion && (
          <CompletionSummary 
            formData={formData} 
            onClose={handleCompletionClose}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default OnboardingAgentInterface;